import React, { useEffect, useState } from "react";
import Card, { CardBody, CardHeader } from "../../ui/Card/Card";
import Button from "../../ui/Button/Button";
import "./AdminPanel.css";
import { PRODUCT_CATEGORIES } from "../../../data/products";
import { uploadImages, createProduct, listProducts, updateProduct, deleteProductById } from "../../../services/firebase";
import type { Product } from "../../../services/firebase";


const AdminPanel: React.FC = () => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedMainCat, setSelectedMainCat] = useState("");
  const [selectedSubCat, setSelectedSubCat] = useState("");
  
  // Helper to find category IDs from name
  const findCategoryIdsFromName = (catName: string) => {
    if (!catName) return { main: "", sub: "" };
    const norm = (s: string) => s.toLowerCase().trim();
    const target = norm(catName);
    
    for (const c of PRODUCT_CATEGORIES) {
      if (norm(c.name) === target) return { main: c.id, sub: "" };
      if (c.subcategories) {
        for (const s of c.subcategories) {
          if (norm(s.name) === target) return { main: c.id, sub: s.id };
        }
      }
    }
    return { main: "", sub: "" };
  };

  type DraftItem = { type: 'existing'; url: string } | { type: 'new'; file: File; preview: string };
  const [galleryDraft, setGalleryDraft] = useState<DraftItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [activeFlag, setActiveFlag] = useState<boolean>(true);

  const fetchProducts = async () => {
    try {
      const data = await listProducts();
      setProducts(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!name.trim()) {
        throw new Error('Ürün adı zorunludur');
      }
      const firstNew = galleryDraft.find(d => d.type === 'new') as { type: 'new'; file: File } | undefined;
      if (firstNew && !/^image\/(png|jpe?g|webp)$/i.test(firstNew.file.type)) {
        throw new Error('Görsel türü yalnızca JPG/PNG/WebP olmalı');
      }
      const newFiles = galleryDraft.filter(d => d.type === 'new').map(d => (d as any).file) as File[];
      const uploaded = newFiles.length ? await uploadImages(newFiles) : [];
      let uploadIdx = 0;
      const finalGallery: string[] = galleryDraft.map(d => d.type === 'existing' ? (d as any).url : uploaded[uploadIdx++]).filter(Boolean);
      const imageUrl = finalGallery[0] || null;
      
      // Determine category string
      let finalCategory = "";
      if (selectedMainCat) {
        const main = PRODUCT_CATEGORIES.find((c: any) => c.id === selectedMainCat);
        if (main) {
          finalCategory = main.name;
          if (selectedSubCat) {
            const sub = main.subcategories?.find((s: any) => s.id === selectedSubCat);
            if (sub) finalCategory = sub.name;
          }
        }
      }

      if (editingId) {
        await updateProduct(editingId, {
          name,
          description,
          category: finalCategory,
          active: activeFlag,
          image_url: imageUrl || undefined,
          gallery: finalGallery,
        });
      } else {
        const created = await createProduct({
          name,
          description,
          category: finalCategory,
          active: activeFlag,
          image_url: imageUrl,
          gallery: finalGallery.length ? finalGallery : null,
        });
        setProducts([created, ...products]);
      }
      setSuccess(editingId ? 'Ürün güncellendi' : 'Ürün başarıyla eklendi');
      setName("");
      setDescription("");
      setSelectedMainCat("");
      setSelectedSubCat("");
      setGalleryDraft([]);
      setActiveFlag(true);
      if (editingId) {
        setProducts(products.map(p => {
          if (p.id !== editingId) return p;
          return { ...p, name, description, category: finalCategory, active: activeFlag, image_url: imageUrl || p.image_url, gallery: finalGallery } as any;
        }));
        setEditingId(null);
      }
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen hata');
    } finally {
      setLoading(false);
    }
  };

  const handleFilesChange = (files: File[]) => {
    const readers: Promise<DraftItem>[] = files.map((f) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res({ type: 'new', file: f, preview: String(r.result) });
      r.readAsDataURL(f);
    }));
    Promise.all(readers).then(items => setGalleryDraft(prev => [...prev, ...items]));
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const fl = Array.from(e.dataTransfer.files || []);
    const imgs = fl.filter(f => /^image\//i.test(f.type));
    handleFilesChange(imgs);
  };

  const startEdit = (p: Product) => {
    setEditingId(p.id);
    setName(p.name);
    setDescription(p.description || "");
    const { main, sub } = findCategoryIdsFromName(p.category || "");
    setSelectedMainCat(main);
    setSelectedSubCat(sub);
    const existing: DraftItem[] = ((p as any).gallery as string[] | undefined)?.map((u) => ({ type: 'existing', url: u }))
      || (p.image_url ? [{ type: 'existing', url: p.image_url }] : []);
    setGalleryDraft(existing);
    setActiveFlag(p.active !== false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setName("");
    setDescription("");
    setSelectedMainCat("");
    setSelectedSubCat("");
    setGalleryDraft([]);
    setActiveFlag(true);
  };

  const deleteProduct = async (id: string) => {
    try {
      await deleteProductById(id);
      setProducts(products.filter(p => p.id !== id));
    } catch (e: any) {
      setError(e.message || 'Silme hatası');
    }
  };

  const toggleActive = async (p: Product) => {
    try {
      const next = !(p as any).active;
      await updateProduct(p.id, { active: next });
      setProducts(products.map(x => x.id === p.id ? { ...x, active: next } : x));
    } catch (e: any) {
      setError(e.message || 'Durum güncelleme hatası');
    }
  };

  return (
    <section id="admin" className="admin">
      <div className="container">
        <Card variant="elevated" className="admin__card">
          <CardHeader>
            <h2>{editingId ? 'Ürünü Düzenle' : 'Admin Paneli'}</h2>
            <p>{editingId ? 'Mevcut ürünü güncelle' : 'Ürün adı, açıklama ve görsel ekleyin'}</p>
          </CardHeader>
          <CardBody>
            <form className="admin__form" onSubmit={handleSubmit}>
              <div className="admin__form-row">
                <label>Ürün Adı *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Örn. Subap Taşlama Makinesi"
                />
              </div>
              <div className="admin__form-row">
                <label>Ana Kategori</label>
                <select
                  value={selectedMainCat}
                  onChange={(e) => {
                    setSelectedMainCat(e.target.value);
                    setSelectedSubCat("");
                  }}
                  className="admin__select"
                >
                  <option value="">Kategori Seçin</option>
                  {PRODUCT_CATEGORIES.map((c: any) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              
              {selectedMainCat && (
                <div className="admin__form-row">
                  <label>Alt Kategori (İsteğe bağlı)</label>
                  <select
                    value={selectedSubCat}
                    onChange={(e) => setSelectedSubCat(e.target.value)}
                    className="admin__select"
                  >
                    <option value="">Alt Kategori Seçin (Opsiyonel)</option>
                    {PRODUCT_CATEGORIES.find((c: any) => c.id === selectedMainCat)?.subcategories?.map((s: any) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="admin__form-row">
                <label>Açıklama</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  placeholder="Ürün açıklaması"
                />
                <div className="admin__char-counter">{description.length} karakter</div>
              </div>
              <div className="admin__form-row admin__form-row--inline">
                <label>Durum</label>
                <label className="admin__switch">
                  <input type="checkbox" checked={activeFlag} onChange={(e) => setActiveFlag(e.target.checked)} />
                  <span className="admin__switch-slider"></span>
                  <span className="admin__switch-label">{activeFlag ? 'Aktif' : 'Pasif'}</span>
                </label>
              </div>
              <div className="admin__form-row">
                <label>Görsel (jpg/png/webp)</label>
                <div
                  className={`upload-dropzone ${dragActive ? 'upload-dropzone--active' : ''}`}
                  onDragEnter={handleDrag}
                  onDragOver={handleDrag}
                  onDragLeave={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFilesChange(Array.from(e.target.files || []))}
                  />
                  <div className="upload-instructions">
                    <i className="fas fa-cloud-upload-alt"></i>
                    <span>Dosyayı sürükleyip bırakın veya tıklayıp seçin</span>
                  </div>
                </div>
                {galleryDraft.length > 0 && (
                  <div className="upload-preview upload-preview--grid">
                    {galleryDraft.map((it, idx) => (
                      <div className="upload-preview__item" key={idx}>
                        <img src={it.type === 'existing' ? (it as any).url : (it as any).preview} alt="Önizleme" />
                        <div className="upload-preview__tools">
                          <button type="button" className="button button--sm" onClick={() => setGalleryDraft(d => {
                            if (idx <= 0) return d;
                            const next = d.slice();
                            const t = next[idx-1]; next[idx-1] = next[idx]; next[idx] = t; return next;
                          })}>←</button>
                          <button type="button" className="button button--sm" onClick={() => setGalleryDraft(d => {
                            if (idx >= d.length-1) return d;
                            const next = d.slice();
                            const t = next[idx+1]; next[idx+1] = next[idx]; next[idx] = t; return next;
                          })}>→</button>
                          <button type="button" className="button button--sm button--outline" onClick={() => setGalleryDraft(d => d.filter((_,i) => i!==idx))}>Sil</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="admin__form-actions">
                <Button>
                  {loading ? 'Kaydediliyor...' : (editingId ? 'Değişiklikleri Kaydet' : 'Ürünü Ekle')}
                </Button>
                {editingId && (
                  <Button variant="ghost" type="button" onClick={cancelEdit}>İptal</Button>
                )}
              </div>
              {error && <p className="admin__error">{error}</p>}
              {success && (
                <div className="admin__success-banner">
                  <i className="fas fa-check-circle"></i>
                  <span>{success}</span>
                </div>
              )}
            </form>
          </CardBody>
        </Card>

        <Card variant="outlined" className="admin__list-card">
          <CardHeader>
            <h3>Mevcut Ürünler</h3>
            <p>Yeni eklediklerin en üstte görünür</p>
          </CardHeader>
          <CardBody>
            <div className="admin__list">
              {products.length === 0 && (
                <p>Henüz ürün eklenmemiş.</p>
              )}
              {products.map((p) => (
                <div key={p.id} className={`admin__list-item ${(p as any).active === false ? 'admin__list-item--inactive' : ''}`}>
                  {p.image_url ? (
                    <div className="admin__thumb">
                      <img src={`${p.image_url || ''}`} alt={p.name} className="admin__list-thumb" />
                      </div>
                  ) : (
                    <div className="admin__thumb admin__thumb--placeholder">
                      <div className="admin__list-thumb admin__list-thumb--placeholder" />
                    </div>
                  )}
                  <div className="admin__list-info">
                    <div className="admin__item-header">
                      <strong className="admin__item-title" title={p.name}>{p.name}</strong>
                      {p.category && <span className="admin__badge">{p.category}</span>}
                      {p.created_at && <small className="admin__meta">{new Date(p.created_at).toLocaleDateString()}</small>}
                    </div>
                    {p.description && <p className="admin__desc">{p.description}</p>}
                    <div className="admin__item-actions">
                      <button className="button button--secondary button--sm" onClick={() => startEdit(p)}>
                        Düzenle
                      </button>
                      <button className="button button--outline button--sm" onClick={() => toggleActive(p)}>
                        {((p as any).active === false) ? 'Aktif Et' : 'Pasif Et'}
                      </button>
                      <button className="button button--secondary button--sm" onClick={() => deleteProduct(p.id)}>
                        Sil
                      </button>
                      <a className="button button--outline button--sm" href={`/products?category=${encodeURIComponent(p.category || '')}`}>Ürünler Sayfasında Gör</a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      </div>
    </section>
  );
};

export default AdminPanel;